import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircle, GitBranch } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarDistancia, mensagemDeErro } from '@/lib/format';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { nomeDaEquipe, useOpcoesDeFormulario } from '@/features/tickets/opcoes';
import {
  chaves,
  escalonar,
  listarEscalonamentos,
} from '@/features/tickets/tickets.api';
import type { CategoriaDeStatus, Escalonamento } from '@/features/tickets/types';

const ENCERRADO: CategoriaDeStatus[] = ['CLOSED', 'CANCELLED'];

export function EscalationPanel({
  ticketId,
  statusCategory,
}: {
  ticketId: string;
  statusCategory: CategoriaDeStatus;
}) {
  const clienteDeConsulta = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: chaves.escalonamentos(ticketId),
    queryFn: () => listarEscalonamentos(ticketId),
  });

  const escalar = useMutation<void, Error, void>({
    mutationFn: () => escalonar(ticketId, motivo.trim() ? { reason: motivo.trim() } : {}),
    onSuccess: () => {
      setMotivo('');
      setErroLocal(null);
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.escalonamentos(ticketId) });
      // O chamado muda de equipe — invalida a ficha principal.
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.item(ticketId) });
    },
    onError: (e) => setErroLocal(mensagemDeErro(e)),
  });

  const pessoas = useDicionarioDePessoas(
    consulta.data?.map((e) => e.escalatedBy).filter(Boolean) as string[] ?? [],
  );

  const opcoes = useOpcoesDeFormulario();
  const encerrado = ENCERRADO.includes(statusCategory);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErroLocal(null);
    escalar.mutate();
  }

  return (
    <div className="space-y-6">
      {/* ── formulário de escalonamento ── */}
      {!encerrado ? (
        <form onSubmit={enviar} className="space-y-3 rounded-md border border-border bg-background p-4">
          <p className="text-sm font-medium">Escalonar chamado</p>
          <p className="text-xs text-muted-foreground">
            O chamado será transferido ao time de nível superior configurado e o responsável será
            desatribuído. A operação é registrada na trilha de auditoria.
          </p>
          <div className="space-y-1">
            <label htmlFor="motivo-escalonamento" className="text-xs font-medium">
              Motivo <span className="text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              id="motivo-escalonamento"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Descreva por que o chamado precisa ser escalonado…"
              maxLength={500}
              rows={2}
              className={cn(
                'w-full resize-none rounded-lg border border-input/80 bg-muted/40 px-3 py-2',
                'text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted',
                'focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow',
              )}
            />
          </div>
          {erroLocal ? (
            <p role="alert" className="text-xs text-destructive">
              {erroLocal}
            </p>
          ) : null}
          <Button
            type="submit"
            size="sm"
            icon={<ArrowUpCircle className="h-4 w-4" aria-hidden />}
            disabled={escalar.isPending}
          >
            {escalar.isPending ? 'Escalonando…' : 'Escalonar'}
          </Button>
        </form>
      ) : null}

      {/* ── histórico ── */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Histórico de escalonamentos
        </p>

        {consulta.isPending ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : consulta.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {mensagemDeErro(consulta.error)}
          </p>
        ) : consulta.data.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
            <GitBranch className="h-5 w-5 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">Nenhum escalonamento registrado.</p>
          </div>
        ) : (
          <ol className="space-y-3">
            {consulta.data.map((item, index) => (
              <ItemDeEscalonamento
                key={item.id}
                item={item}
                numero={consulta.data.length - index}
                pessoas={pessoas}
                opcoes={opcoes.data}
                carregandoOpcoes={opcoes.isPending}
              />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function ItemDeEscalonamento({
  item,
  numero,
  pessoas,
  opcoes,
  carregandoOpcoes,
}: {
  item: Escalonamento;
  numero: number;
  pessoas: ReturnType<typeof useDicionarioDePessoas>;
  opcoes: ReturnType<typeof useOpcoesDeFormulario>['data'];
  carregandoOpcoes: boolean;
}) {
  const nomeOrigem = item.fromTeamId
    ? (nomeDaEquipe(opcoes, item.fromTeamId) ?? (carregandoOpcoes ? '…' : item.fromTeamId.slice(0, 8)))
    : '—';
  const nomeDestino =
    nomeDaEquipe(opcoes, item.toTeamId) ??
    (carregandoOpcoes ? '…' : item.toTeamId.slice(0, 8));

  return (
    <li className="rounded-md border border-border bg-background p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warning/15 text-[10px] font-bold tabular-nums text-warning ring-1 ring-warning/30">
            {numero}
          </span>
          <span className="font-medium">
            {nomeOrigem}
            <span className="mx-1.5 text-muted-foreground">→</span>
            {nomeDestino}
          </span>
        </div>
        {item.automatic ? (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-inset ring-border">
            automático
          </span>
        ) : null}
      </div>

      {item.reason ? (
        <p className="mt-2 text-muted-foreground">{item.reason}</p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span title={formatarDataHora(item.createdAt)}>{formatarDistancia(item.createdAt)}</span>
        {item.escalatedBy ? (
          <span>
            por <NomeDePessoa id={item.escalatedBy} pessoas={pessoas} />
          </span>
        ) : null}
      </div>
    </li>
  );
}
